/**
 * 搜索面板 — 三栏布局 + Shopify suggest.json
 */

class SearchPanel {
  constructor() {
    this.cfg = window.searchPanelConfig || {};
    this.panel = document.getElementById('searchPanel');
    this.overlay = document.getElementById('searchPanelOverlay');
    this.input = document.getElementById('searchPanelInput');
    this.clearBtn = document.getElementById('searchPanelClear');
    this.grid = document.getElementById('searchPanelGrid');
    this.loading = document.getElementById('searchLoading');
    this.noResults = document.getElementById('searchNoResults');
    this.footerBar = document.getElementById('searchPanelFooterBar');
    this.viewAllLink = document.getElementById('viewAllResults');

    this.idleLeft = document.getElementById('searchIdleLeft');
    this.idleMiddle = document.getElementById('searchIdleMiddle');
    this.idleProducts = document.getElementById('searchIdleProducts');
    this.liveSuggestions = document.getElementById('searchLiveSuggestions');
    this.suggestionsList = document.getElementById('suggestionsList');
    this.liveMiddle = document.getElementById('searchLiveMiddle');
    this.categoriesList = document.getElementById('categoriesList');
    this.brandsList = document.getElementById('brandsList');
    this.liveProducts = document.getElementById('searchLiveProducts');
    this.productsList = document.getElementById('productsList');

    this.searchTimeout = null;
    this.debounceDelay = 280;
    this.prefetchTimeout = null;
    this.prefetchDelay = 2000;
    this._searchId = 0;
    this.minChars = 2;
    this.historyKey = 'shopify_search_history';
    this.maxHistoryItems = 8;
    this.navCacheKey = 'search_panel_nav_index_v2';
    this.navIndex = [];
    this.idleProductsHtml = this.idleProducts?.innerHTML || '';

    // DEBUG: set to true or add ?searchdebug=1 to URL
    this.DEBUG = new URLSearchParams(window.location.search).get('searchdebug') === '1';
    if (this.DEBUG) {
      console.log('[SearchPanel] window.searchPanelConfig.navigationData:', JSON.stringify(window.searchPanelConfig.navigationData, null, 2));
    }

    this.applyStaticStrings();
    this.wrapIdleProducts();
    this.init();
  }

  applyStaticStrings() {
    const s = this.cfg.strings || {};
    const setText = (id, text) => {
      const el = document.getElementById(id);
      if (el && text) el.textContent = text;
    };
    setText('searchSuggestionsHeading', s.searchSuggestions);
    setText('hotSearchHeading', s.hotSearch);
    setText('recentHeading', s.recent);
    setText('collectionsHeading', s.collections);
    const clearBtn = document.getElementById('clearHistory');
    if (clearBtn && s.clearHistory) clearBtn.textContent = s.clearHistory;
    const noTitle = document.getElementById('noResultsTitle');
    const noHint = document.getElementById('noResultsHint');
    if (noTitle && s.noResults) noTitle.textContent = s.noResults;
    if (noHint && s.tryDifferent) noHint.textContent = s.tryDifferent;
  }

  wrapIdleProducts() {
    const container = this.idleProducts;
    if (!container) return;
    const title = container.querySelector('.search-col-title');
    const stack = container.querySelector('.search-product-stack');
    if (!stack || container.querySelector('.search-product-scroll')) return;

    const scrollDiv = document.createElement('div');
    scrollDiv.className = 'search-product-scroll';
    scrollDiv.appendChild(stack.cloneNode(true));
    container.innerHTML = '';
    if (title) container.appendChild(title.cloneNode(true));
    container.appendChild(scrollDiv);
  }

  getSuggestEndpoint() {
    const base = this.cfg.suggestUrl || window.routes?.predictive_search_url || '/search/suggest';
    return base.includes('.json') ? base : `${base.replace(/\/?$/, '')}.json`;
  }

  getSearchUrl() {
    return this.cfg.searchUrl || '/search';
  }

  init() {
    this.bootstrapNavigationIndex();
    this.bindEvents();
    this.loadHistory();
  }

  bootstrapNavigationIndex() {
    const navData = this.cfg.navigationData;
    if (navData && Object.keys(navData).length > 0) {
      this.navIndex = this.buildNavIndexFromConfig();
      if (this.DEBUG) {
        console.log('[SearchPanel] navIndex built from navigationData:', JSON.stringify(this.navIndex, null, 2));
        console.log('[SearchPanel] navData keys:', Object.keys(navData));
        const v = navData['vanities & storage'];
        console.log('[SearchPanel] navData["vanities & storage"]:', JSON.stringify(v, null, 2));
      }
      return;
    }
    const cached = this.loadNavIndexFromCache();
    if (cached.length) {
      this.navIndex = cached;
      if (this.DEBUG) {
        console.log('[SearchPanel] navIndex loaded from cache:', JSON.stringify(this.navIndex, null, 2));
      }
      return;
    }
    const parsed = this.parseNavigationFromDom();
    if (parsed.length) {
      this.navIndex = parsed;
      this.saveNavIndexToCache(parsed);
      if (this.DEBUG) {
        console.log('[SearchPanel] navIndex built from DOM:', JSON.stringify(this.navIndex, null, 2));
      }
    }
  }

  loadNavIndexFromCache() {
    try {
      const raw = localStorage.getItem(this.navCacheKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  saveNavIndexToCache(index) {
    try {
      localStorage.setItem(this.navCacheKey, JSON.stringify(index));
    } catch (e) {
      // ignore storage errors
    }
  }

  buildNavIndexFromConfig() {
    const navData = this.cfg.navigationData || {};
    return Object.values(navData)
      .filter((group) => Array.isArray(group?.children) && group.children.length > 0)
      .map((group) => ({
        title: group.title || '',
        url: group.url || '',
        items: group.children.map((child) => ({
          title: child.title || '',
          url: child.url || '',
          children: Array.isArray(child.children) ? child.children.map((sub) => ({
            title: this.normalizeChildTitle(sub.title || '', child.title || '', group.title || ''),
            url: sub.url || ''
          })) : []
        }))
      }));
  }

  normalizeChildTitle(subTitle, childTitle, groupTitle) {
    const sub = subTitle.toLowerCase();
    const child = childTitle.toLowerCase();

    if (sub.includes(child)) return subTitle;

    if (child === 'baths') {
      if (sub.includes('bathtubs')) return subTitle;
      if (/\bbath\b/i.test(subTitle)) return subTitle.replace(/\bbath\b/i, 'Bathtub');
      return `${subTitle} Bathtubs`;
    }

    return `${subTitle} ${child}`;
  }

  parseNavigationFromDom() {
    return [];
  }

  bindEvents() {
    document.querySelectorAll('#header-search, .search-header a').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.panel?.querySelector('.search-panel-close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    this.input?.addEventListener('input', (e) => this.handleInput(e));
    this.input?.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.clearBtn?.addEventListener('click', () => this.clearInput());

    document.getElementById('clearHistory')?.addEventListener('click', () => this.clearHistory());

    document.querySelectorAll('#searchTrending .search-link').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const q = link.textContent.trim();
        if (this.input) {
          this.input.value = q;
          this.toggleClearButton();
          this.handleInput({ target: this.input });
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel?.classList.contains('active')) this.close();
    });
  }

  open() {
    if (!this.panel) return;

    // Always capture current scroll position before locking
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;
    
    // If already locked (from a previous open), restore first to get fresh position
    if (document.body.style.position === 'fixed') {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.documentElement.style.position = '';
      document.documentElement.style.top = '';
      document.documentElement.style.width = '';
      window.scrollTo(currentScrollX, currentScrollY);
    }

    this._scrollY = currentScrollY;
    this._scrollX = currentScrollX;

    this.panel.classList.add('active');
    this.overlay?.classList.add('active');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-panel-open');

    // Remove no-scroll class if present (from mobile menu, cart, etc.)
    // The search panel uses position:fixed for its own scroll lock
    document.body.classList.remove('no-scroll');

    // Lock scroll using position: fixed
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.width = '100%';
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.top = `-${this._scrollY}px`;
    document.documentElement.style.width = '100%';

    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    if (!this.panel) return;
    this.panel.classList.remove('active');
    this.overlay?.classList.remove('active');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-panel-open');

    // Restore scroll position and remove fixed positioning
    const scrollY = this._scrollY || 0;
    const scrollX = this._scrollX || 0;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.position = '';
    document.documentElement.style.top = '';
    document.documentElement.style.width = '';

    // Always clear no-scroll on close (let other components add it back if needed)
    document.body.classList.remove('no-scroll');

    // Restore scroll position after a tiny delay to ensure DOM is ready
    requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
    });

    this.resetPanel();
  }

  clearInput() {
    if (this.input) this.input.value = '';
    this.toggleClearButton();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.clearPrefetchProducts();
    this.resetIdleProducts();
    this.showIdleState();
    this.hideLoading();
    this.noResults?.classList.add('hidden');
    this.footerBar?.classList.add('hidden');
  }

  resetPanel() {
    this.clearInput();
  }

  toggleClearButton() {
    const has = this.input?.value?.length > 0;
    this.clearBtn?.classList.toggle('hidden', !has);
  }

  handleInput(e) {
    const query = e.target.value.trim();
    this.toggleClearButton();

    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (this.prefetchTimeout) clearTimeout(this.prefetchTimeout);

    if (query.length < this.minChars) {
      this.clearPrefetchProducts();
      this.showIdleState();
      this.hideLoading();
      this.noResults?.classList.add('hidden');
      this.footerBar?.classList.add('hidden');
      return;
    }

    // 280ms debounce for quick search
    this.searchTimeout = setTimeout(() => this.performSearch(query), this.debounceDelay);

    // 2s delay for silent product prefetch (show in idle products column)
    this.prefetchTimeout = setTimeout(() => {
      this.prefetchProducts(query);
    }, this.prefetchDelay);
  }

  handleKeydown(e) {
    if (e.key !== 'Enter') return;
    const query = this.input?.value?.trim();
    if (!query) return;
    e.preventDefault();
    this.addToHistory(query);
    this.input?.form?.submit();
  }

  showIdleState() {
    this.idleLeft?.classList.remove('hidden');
    this.idleMiddle?.classList.remove('hidden');
    this.idleProducts?.classList.remove('hidden');
    this.liveSuggestions?.classList.add('hidden');
    this.liveMiddle?.classList.add('hidden');
    this.liveProducts?.classList.add('hidden');
    this.grid?.classList.remove('hidden');
  }

  showLiveLayout() {
    this.idleLeft?.classList.add('hidden');
    this.idleMiddle?.classList.add('hidden');
    this.idleProducts?.classList.add('hidden');
    this.liveSuggestions?.classList.remove('hidden');
    this.liveMiddle?.classList.remove('hidden');
    this.liveProducts?.classList.remove('hidden');
    this.grid?.classList.remove('hidden');
  }

  async performSearch(query) {
    if (this.DEBUG) {
      console.log('[SearchPanel] performSearch called with:', query);
    }
    this.clearPrefetchProducts();
    this.footerBar?.classList.add('hidden');
    this.showLiveLayout();
    this.noResults?.classList.add('hidden');
    this.loading?.classList.remove('hidden');

    // Increment search ID to cancel any in-flight search
    const searchId = ++this._searchId;

    try {
      const [suggestData, productsData] = await Promise.all([
        this.fetchSuggest(query),
        this.fetchProductsOnly(query)
      ]);
      
      // Ignore stale results
      if (searchId !== this._searchId) return;
      
      // Merge products from both API calls
      if (suggestData?.resources?.results?.products && productsData?.length > 0) {
        const existingUrls = new Set(suggestData.resources.results.products.map(p => p.url));
        const newProducts = productsData.filter(p => !existingUrls.has(p.url));
        suggestData.resources.results.products = [
          ...suggestData.resources.results.products,
          ...newProducts
        ];
      }
      
      this.renderResults(suggestData, query);
    } catch (err) {
      console.error('Search panel:', err);
      this.hideLoading();
      this.noResults?.classList.remove('hidden');
      this.updateFooter(query);
    }
  }

  async prefetchProducts(query) {
    // Silent fetch: only products, limit 15, no loading indicator
    // Cancel if a full search has already started
    if (this.searchTimeout) return;

    try {
      const products = await this.fetchProductsOnly(query);
      if (products.length > 0) {
        this.renderPrefetchProducts(products);
      }
    } catch (err) {
      // Silently fail
    }
  }

  async fetchProductsOnly(query) {
    const endpoint = this.getSuggestEndpoint();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '30',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,vendor,product_type,variants.sku',
    });

    const res = await fetch(`${endpoint}?${params.toString()}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data?.resources?.results?.products || [];
  }

  renderPrefetchProducts(products) {
    const container = document.getElementById('searchIdleProducts');
    if (!container) return;

    const title = container.querySelector('.search-col-title');
    const html = this.renderProducts(products);

    container.innerHTML = '';
    if (title) container.appendChild(title.cloneNode(true));
    const scrollDiv = document.createElement('div');
    scrollDiv.className = 'search-product-scroll';
    scrollDiv.innerHTML = `<div class="search-product-stack">${html}</div>`;
    container.appendChild(scrollDiv);
  }

  clearPrefetchProducts() {
    if (this.prefetchTimeout) {
      clearTimeout(this.prefetchTimeout);
      this.prefetchTimeout = null;
    }
  }

  resetIdleProducts() {
    if (this.idleProducts && this.idleProductsHtml) {
      const title = this.idleProducts.querySelector('.search-col-title');
      const originalStack = this.idleProducts.querySelector('.search-product-stack');

      this.idleProducts.innerHTML = this.idleProductsHtml;

      if (originalStack) {
        const stackClone = originalStack.cloneNode(true);
        this.idleProducts.innerHTML = '';
        if (title) this.idleProducts.appendChild(title.cloneNode(true));
        const scrollDiv = document.createElement('div');
        scrollDiv.className = 'search-product-scroll';
        scrollDiv.appendChild(stackClone);
        this.idleProducts.appendChild(scrollDiv);
      }
    }
  }

  async fetchSuggest(query) {
    const endpoint = this.getSuggestEndpoint();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'query,product,collection',
      'resources[limit]': '50',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,vendor,product_type,variants.sku',
    });

    if (this.DEBUG) {
      console.log('[SearchPanel] fetchSuggest URL:', `${endpoint}?${params.toString()}`);
    }

    const res = await fetch(`${endpoint}?${params.toString()}`);
    if (!res.ok) throw new Error('suggest failed');
    const data = await res.json();
    
    if (this.DEBUG) {
      console.log('[SearchPanel] fetchSuggest raw response:', data);
      console.log('[SearchPanel] products from API:', data?.resources?.results?.products?.length || 0);
    }
    
    return data;
  }

  renderResults(data, query) {
    this.hideLoading();

    const results = data?.resources?.results || {};
    const queries = results.queries || [];
    const products = results.products || [];
    const collections = results.collections || [];

    if (this.DEBUG) {
      console.log('[SearchPanel] renderResults - products count:', products.length, products);
      console.log('[SearchPanel] renderResults - collections count:', collections.length);
      console.log('[SearchPanel] renderResults - vendors count:', this.uniqueVendors(products).length);
    }

    const vendors = this.uniqueVendors(products);

    const hasAny =
      queries.length + products.length + collections.length + vendors.length > 0;

    if (!hasAny) {
      this.grid?.classList.add('hidden');
      this.noResults?.classList.remove('hidden');
      this.updateFooter(query);
      return;
    }

    this.grid?.classList.remove('hidden');
    this.noResults?.classList.add('hidden');

    if (this.suggestionsList) this.suggestionsList.innerHTML = this.renderQueries(queries, query);
    if (this.categoriesList) this.categoriesList.innerHTML = this.renderCollections(collections, query);
    if (this.brandsList) this.brandsList.innerHTML = this.renderBrands(vendors);
    if (this.productsList) this.productsList.innerHTML = this.renderProducts(products);

    this.bindResultClicks();
    this.updateFooter(query);
  }

  attrSafe(val) {
    return String(val ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  renderQueries(queries, fallbackQuery) {
    if (!queries.length) {
      const url = `${this.getSearchUrl()}?q=${encodeURIComponent(fallbackQuery)}`;
      return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(fallbackQuery)}</a></li>`;
    }
    return queries
      .map((q) => {
        const text = q.text || q.styled_text || '';
        const href = q.url || `${this.getSearchUrl()}?q=${encodeURIComponent(text)}`;
        return `<li><a class="search-link" href="${this.attrSafe(href)}">${this.escapeHtml(text)}</a></li>`;
      })
      .join('');
  }

  renderCollections(collections, query) {
    const keywordMap = this.buildKeywordMap(query);
    if (keywordMap.length > 0) {
      return keywordMap.map((item) => {
        const url = item.url || `${this.getSearchUrl()}?q=${encodeURIComponent(item.urlParam || item.title)}`;
        return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(item.title)}</a></li>`;
      }).join('');
    }
    if (!collections.length) {
      return `<li class="search-link-list__empty">—</li>`;
    }
    return collections
      .map((c) => {
        const title = c.title || '';
        const url = c.url || '#';
        return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(title)}</a></li>`;
      })
      .join('');
  }

  buildKeywordMap(query) {
    const q = query || '';
    if (this.DEBUG) {
      console.log('[SearchPanel] buildKeywordMap query="' + q + '", navIndex=', JSON.stringify(this.navIndex, null, 2));
    }
    const match = this.findBestNavGroupMatch(q);
    if (match) {
      // Prefer children (2nd-level sub-items), fallback to items (group root direct children)
      const subItems = (match.item.children && match.item.children.length > 0)
        ? match.item.children
        : (match.item.items || []);

      if (subItems.length) {
        if (this.DEBUG) {
          console.log('[SearchPanel] matched item "' + match.item.title + '" → sub-items:', subItems);
        }
        return subItems.map((sub) => ({
          title: sub.title,
          url: sub.url || `${this.getSearchUrl()}?q=${encodeURIComponent(sub.title)}`
        }));
      }

      // Sub-item matched but has no children → walk back up to its group's items
      if (match.item.children !== undefined && !(match.item.children && match.item.children.length > 0)) {
        const parentGroup = this.navIndex.find((g) => g.title === match.groupTitle);
        const parentItems = (parentGroup && parentGroup.items) ? parentGroup.items : [];
        if (parentItems.length) {
          if (this.DEBUG) {
            console.log('[SearchPanel] matched sub-item "' + match.item.title + '" with no children, fallback to group "' + match.groupTitle + '" items:', parentItems);
          }
          return parentItems.map((sub) => ({
            title: sub.title,
            url: sub.url || `${this.getSearchUrl()}?q=${encodeURIComponent(sub.title)}`
          }));
        }
      }

      if (this.DEBUG) {
        console.log('[SearchPanel] matched item "' + match.item.title + '" but has no sub-items, fallback to item itself');
      }
      return [{
        title: match.item.title,
        url: match.item.url || `${this.getSearchUrl()}?q=${encodeURIComponent(match.item.title)}`
      }];
    }
    if (this.DEBUG) {
      console.log('[SearchPanel] no nav match for "' + q + '"');
    }
    return [];
  }

  findBestNavGroupMatch(query) {
    if (!this.navIndex.length) return null;
    const qNorm = this.normalizeTerm(query);
    if (!qNorm) return null;

    let best = null;
    let bestScore = 0;
    let bestLevel = 0;

    this.navIndex.forEach((group) => {
      const groupNorm = this.normalizeTerm(group.title);
      const groupSing = this.singularTokenMatch(qNorm, groupNorm);
      const groupScore = this.calcMatchScore(qNorm, groupNorm, groupSing);
      const groupLevel = this.matchLevel(qNorm, groupNorm, groupSing);

      if (groupScore > bestScore || (groupScore === bestScore && groupLevel > bestLevel)) {
        bestScore = groupScore;
        bestLevel = groupLevel;
        best = { groupTitle: group.title, item: group };
      }

      group.items.forEach((item) => {
        const itemNorm = this.normalizeTerm(item.title);
        if (!itemNorm) return;
        const itemSing = this.singularTokenMatch(qNorm, itemNorm);
        const itemScore = this.calcMatchScore(qNorm, itemNorm, itemSing);
        const itemLevel = this.matchLevel(qNorm, itemNorm, itemSing);

        if (itemScore > bestScore || (itemScore === bestScore && itemLevel > bestLevel)) {
          bestScore = itemScore;
          bestLevel = itemLevel;
          best = { groupTitle: group.title, item };
        }
      });
    });

    if (this.DEBUG) {
      console.log('[SearchPanel] findBestNavMatch for "' + query + '": best=' + JSON.stringify(best) + ', score=' + bestScore + ', level=' + bestLevel);
    }

    return bestScore >= 80 ? best : null;
  }

  normalizeTerm(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  singularizeToken(token) {
    if (token.endsWith('ies') && token.length > 3) return `${token.slice(0, -3)}y`;
    if (token.endsWith('es') && token.length > 3) return token.slice(0, -2);
    if (token.endsWith('s') && token.length > 3) return token.slice(0, -1);
    return token;
  }

  toSearchTokens(text) {
    const normalized = this.normalizeTerm(text);
    if (!normalized) return [];
    const tokens = normalized.split(' ').filter(Boolean);
    const expanded = new Set();
    tokens.forEach((token) => {
      expanded.add(token);
      expanded.add(this.singularizeToken(token));
    });
    return Array.from(expanded).filter(Boolean);
  }

  singularTokenMatch(qNorm, itemNorm) {
    if (!qNorm || !itemNorm) return 0;
    const qTokens = qNorm.split(' ').filter(Boolean);
    const itemTokens = itemNorm.split(' ').filter(Boolean);
    for (const qt of qTokens) {
      const qtSing = this.singularizeToken(qt);
      for (const it of itemTokens) {
        const itSing = this.singularizeToken(it);
        // Exact singular match at token level: "bathtubs" → "bathtub"
        if (qtSing === itSing) return 2;
        // Prefix singular match: "bathtubs" starts with "bathtub"
        if (qtSing.length >= 4 && itSing.length >= 4 && (qtSing.startsWith(itSing) || itSing.startsWith(qtSing))) return 1;
      }
    }
    return 0;
  }

  singularSubstringMatch(qNorm, itemNorm) {
    if (!qNorm || !itemNorm) return false;
    if (itemNorm.includes(qNorm) || qNorm.includes(itemNorm)) return true;
    const qSing = this.singularizeToken(qNorm);
    const itSing = this.singularizeToken(itemNorm);
    if (itSing.includes(qSing) || qSing.includes(itSing)) return true;
    return false;
  }

  matchLevel(qNorm, norm, singMatch) {
    // Higher = better match type. Used to break ties at same score.
    // 5: exact singular (same word stem, e.g. "bathtubs" → "bathtub")
    // 4: exact string
    // 3: singular substring
    // 2: singular prefix (same 4-char stem)
    // 1: approximate
    if (singMatch === 2) return 5;
    if (qNorm === norm) return 4;
    if (this.singularSubstringMatch(qNorm, norm)) return 3;
    if (singMatch === 1) return 2;
    return 1;
  }

  calcMatchScore(qNorm, norm, singMatch) {
    if (singMatch === 2) return 95; // Exact singular: same word stem, highest priority
    if (qNorm === norm) return 100;
    if (singMatch === 1 && this.singularSubstringMatch(qNorm, norm)) return 90; // Singular + substring
    if (singMatch === 1) return 88; // Singular prefix only
    if (this.singularSubstringMatch(qNorm, norm)) return 85; // Substring without singular
    if (this.isApproxMatch(qNorm, norm)) return 80;
    return 0;
  }

  isApproxMatch(query, candidate) {
    const queryNorm = this.normalizeTerm(query);
    const candidateNorm = this.normalizeTerm(candidate);
    if (!queryNorm || !candidateNorm) return false;
    if (queryNorm.includes(candidateNorm) || candidateNorm.includes(queryNorm)) return true;

    const queryTokens = this.toSearchTokens(queryNorm);
    const candidateTokens = this.toSearchTokens(candidateNorm);

    return queryTokens.some((qt) =>
      candidateTokens.some((ct) => {
        if (qt === ct) return true;
        if (qt.length >= 5 && ct.length >= 5 && qt.slice(0, 5) === ct.slice(0, 5)) return true;
        return false;
      })
    );
  }

  uniqueVendors(products) {
    const seen = new Set();
    const out = [];
    products.forEach((p) => {
      const v = (p.vendor || '').trim();
      if (v && !seen.has(v.toLowerCase())) {
        seen.add(v.toLowerCase());
        out.push(v);
      }
    });
    return out.slice(0, 8);
  }

  renderBrands(vendors) {
    if (!vendors.length) {
      return `<li class="search-link-list__empty">—</li>`;
    }
    return vendors
      .map((v) => {
        const url = `${this.getSearchUrl()}?q=${encodeURIComponent(v)}`;
        return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(v)}</a></li>`;
      })
      .join('');
  }

  renderProducts(products) {
    if (!products.length) {
      return `<p class="search-product-stack__empty">—</p>`;
    }
    const partLabel = this.cfg.strings?.partNo || 'Part No';
    return products
      .map((p) => {
        const title = p.title || '';
        const url = p.url || '#';
        const img =
          p.featured_image?.url ||
          p.image ||
          'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
        const sku = p.variants?.[0]?.sku || p.sku || '';
        const priceHtml = this.formatProductMoney(p);

        const skuLine = sku
          ? `<span class="search-proto-product__sku">${this.escapeHtml(partLabel)}: ${this.escapeHtml(sku)}</span>`
          : '';

        return `
          <a href="${this.attrSafe(url)}" class="search-proto-product">
            <div class="search-proto-product__media">
              <img src="${this.attrSafe(img)}" alt="" loading="lazy" width="72" height="72">
            </div>
            <div class="search-proto-product__body">
              <span class="search-proto-product__title">${this.escapeHtml(title)}</span>
              ${skuLine}
              <span class="search-proto-product__price search-proto-product__price--live">${priceHtml}</span>
            </div>
          </a>
        `;
      })
      .join('');
  }

  formatProductMoney(product) {
    const raw = product.price_min ?? product.price;
    if (raw == null || raw === '') return '';
    const fmt = this.cfg.moneyFormat || window.Shopify?.money_format;
    let cents = null;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      cents = raw;
    } else if (typeof raw === 'string' && raw.includes('.')) {
      const f = parseFloat(raw);
      if (!Number.isNaN(f)) cents = Math.round(f * 100);
    } else {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) cents = n;
    }
    if (cents == null) return this.escapeHtml(String(raw));
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && fmt) {
      return Shopify.formatMoney(cents, fmt);
    }
    return this.escapeHtml(String(raw));
  }

  updateFooter(query) {
    if (!this.viewAllLink || !this.footerBar) return;
    const label = this.cfg.strings?.seeAllFor || 'See all results for';
    this.viewAllLink.textContent = `${label} '${query}'`;
    this.viewAllLink.href = `${this.getSearchUrl()}?q=${encodeURIComponent(query)}`;
    this.footerBar.classList.remove('hidden');
  }

  bindResultClicks() {
    const q = this.input?.value?.trim();
    if (!q) return;
    this.panel
      ?.querySelectorAll(
        '#suggestionsList a, #categoriesList a, #brandsList a, #productsList a.search-proto-product'
      )
      .forEach((el) => {
        el.addEventListener('click', () => this.addToHistory(q));
      });
  }

  hideLoading() {
    this.loading?.classList.add('hidden');
  }

  escapeHtml(str) {
    if (str == null) return '';
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }

  loadHistory() {
    try {
      const history = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
      this.renderHistory(history);
    } catch (e) {
      console.error(e);
    }
  }

  renderHistory(history) {
    const listEl = document.getElementById('historyList');
    const clearBtn = document.getElementById('clearHistory');
    if (!listEl) return;

    if (!history.length) {
      listEl.innerHTML = '';
      clearBtn?.classList.add('hidden');
      return;
    }

    clearBtn?.classList.remove('hidden');
    const base = this.getSearchUrl();
    listEl.innerHTML = history
      .map((item) => {
        const enc = encodeURIComponent(item);
        return `
      <li class="history-list__item">
        <a href="${this.attrSafe(`${base}?q=${enc}`)}" class="search-link">${this.escapeHtml(item)}</a>
        <button type="button" class="history-remove" data-query="${this.attrSafe(enc)}" aria-label="Remove">×</button>
      </li>`;
      })
      .join('');

    listEl.querySelectorAll('.history-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const enc = btn.getAttribute('data-query') || '';
        this.removeFromHistory(decodeURIComponent(enc));
      });
    });
  }

  addToHistory(query) {
    if (!query || query.length < 2) return;
    try {
      let history = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
      history = history.filter((item) => item.toLowerCase() !== query.toLowerCase());
      history.unshift(query);
      history = history.slice(0, this.maxHistoryItems);
      localStorage.setItem(this.historyKey, JSON.stringify(history));
      this.renderHistory(history);
    } catch (e) {
      console.error(e);
    }
  }

  removeFromHistory(query) {
    try {
      let history = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
      history = history.filter((item) => item !== query);
      localStorage.setItem(this.historyKey, JSON.stringify(history));
      this.renderHistory(history);
    } catch (e) {
      console.error(e);
    }
  }

  clearHistory() {
    localStorage.removeItem(this.historyKey);
    this.renderHistory([]);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.searchPanel = new SearchPanel();
});
